import React, { useCallback, useEffect, useState } from 'react';
import { adminService } from '../services/adminService';

const MODES = ['HOLD_EM', 'OMAHA', 'THREE_CARD', 'OMAHA_HI_LO', 'OMAHA_FIVE'] as const;
type Mode = (typeof MODES)[number];
type ModeConfig = Record<string, number>;

const FIELDS = [
  ['deal_interval_seconds', 'Intervalo das cartas', 'segundos'],
  ['street_reveal_delay_seconds', 'Transição de street', 'segundos'],
  ['all_in_street_delay_seconds', 'Intervalo do all-in', 'segundos'],
  ['showdown_delay_seconds', 'Exibição do showdown', 'segundos'],
  ['payout_animation_seconds', 'Animação do pagamento', 'segundos'],
  ['next_hand_delay_seconds', 'Intervalo da próxima mão', 'segundos'],
] as const;

const GameModesConfigPage: React.FC = () => {
  const [modes, setModes] = useState<Record<Mode, ModeConfig> | null>(null);
  const [reason, setReason] = useState('Ajuste profissional dos tempos da mesa');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const overview = await adminService.getConfigOverview();
      const group = overview.global_config.items.find((item) => item.group_key === 'GAME_MODE_TIMINGS');
      const config = (group?.config_json || {}) as { modes?: Record<Mode, ModeConfig> };
      setModes(config.modes || null);
    } catch (loadError: any) {
      setError(loadError.message || 'Não foi possível carregar os modos de jogo');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const updateValue = (mode: Mode, key: string, value: string) => {
    const numeric = Number(value);
    setModes((current) => current ? {
      ...current,
      [mode]: { ...current[mode], [key]: Number.isFinite(numeric) ? numeric : 0 },
    } : current);
  };

  const save = async () => {
    if (!modes || !reason.trim()) return;
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await adminService.patchGlobalConfig({
        groupKey: 'GAME_MODE_TIMINGS',
        configJson: { version: 1, modes },
        reason: reason.trim(),
      });
      setMessage('Configuração salva com trilha de auditoria. Novas mãos usarão os valores atualizados.');
    } catch (saveError: any) {
      setError(saveError.message || 'Não foi possível salvar a configuração');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page-stack">
      <section className="admin-hero-card">
        <p className="admin-section-eyebrow">Operação da mesa</p>
        <h1>Config modos de jogo</h1>
        <p>Controle os intervalos visuais por variante. O timeout efetivo continua sendo o definido em cada mesa.</p>
      </section>
      {error ? <div className="admin-banner admin-banner--error">{error}</div> : null}
      {message ? <div className="admin-banner">{message}</div> : null}
      {modes ? MODES.map((mode) => (
        <section className="admin-section-card" key={mode}>
          <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Modo</p><h2>{mode}</h2></div></div>
          <div className="admin-filter-grid">
            {FIELDS.map(([key, label, unit]) => (
              <label key={key} className="admin-detail-pair">
                <span>{label} ({unit})</span>
                <input className="admin-input" type="number" min="0.25" max="10" step="0.25" value={modes[mode]?.[key] ?? ''} onChange={(event) => updateValue(mode, key, event.target.value)} />
              </label>
            ))}
          </div>
        </section>
      )) : null}
      <section className="admin-section-card">
        <label className="admin-detail-pair"><span>Motivo obrigatório</span><input className="admin-input" value={reason} onChange={(event) => setReason(event.target.value)} /></label>
        <button className="admin-primary-button" type="button" disabled={saving || !modes || !reason.trim()} onClick={() => void save()}>{saving ? 'Salvando...' : 'Salvar configurações'}</button>
      </section>
    </div>
  );
};

export default GameModesConfigPage;
